<template>
  <div class="shipment-details">
    <div class="shipment-row">
      <span class="shipment-key">{{
        t("PAYJOY_SHIPMENT_DETAILS.SHIPPED_CARD_ESTIMATE_DATE")
      }}</span>
      <span class="shipment-value">{{
        formatDeliveryDate(estimatedDeliveryDate)
      }}</span>
    </div>
    <div class="shipment-row" v-if="address">
      <span class="shipment-key">{{
        t("PAYJOY_SHIPMENT_DETAILS.SHIPPED_CARD_ADDRESS")
      }}</span>
      <span class="shipment-value">{{ address }}</span>
    </div>
    <div class="shipment-row shipment-row-tracking">
      <span class="shipment-key">{{
        t("PAYJOY_SHIPMENT_DETAILS.SHIPPED_CARD_PROVIDER", { provider })
      }}</span>
      <span class="shipment-value tracking-value">
        {{ orderNumber }}
        <button
          class="shipment-copy"
          @click="copyOrderToClipboard"
          title="Copiar"
        >
          <i
            class="fa-solid"
            :class="isCartTagCopied ? 'fa-check' : 'fa-copy'"
          ></i>
        </button>
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";

const { t } = useI18n();

const props = defineProps({
  estimatedDeliveryDate: String,
  address: String,
  provider: String,
  orderNumber: String,
});

const isCartTagCopied = ref(false);

function formatDeliveryDate(dateStr: string): string {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const dayKeys = [
    "SUNDAY",
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
    "SATURDAY",
  ];
  const monthKeys = [
    "JANUARY",
    "FEBRUARY",
    "MARCH",
    "APRIL",
    "MAY",
    "JUNE",
    "JULY",
    "AUGUST",
    "SEPTEMBER",
    "OCTOBER",
    "NOVEMBER",
    "DECEMBER",
  ];
  const dayName = t(`PAYJOY_SHIPMENT_DETAILS.${dayKeys[date.getUTCDay()]}`);
  const monthName = t(`PAYJOY_SHIPMENT_DETAILS.${monthKeys[date.getUTCMonth()]}`);
  return `${dayName} ${date.getDate()} de ${monthName} ${date.getUTCFullYear()}`;
}

const copyOrderToClipboard = async () => {
  await navigator.clipboard.writeText(props.orderNumber || "");
  isCartTagCopied.value = true;
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/_constants.scss";

.shipment-details {
  margin-top: 8px;
}

.shipment-row {
  display: grid;
  grid-template-columns: max-content 1fr auto;
  column-gap: 5px;
  align-items: start;
  margin-bottom: 6px;
}
.shipment-row-tracking .tracking-value {
  display: flex;
  align-items: center;
  gap: 8px;
}

.shipment-key {
  grid-column: 1;
  font-weight: 600;
  font-size: 14px;
  line-height: 19px;
  font-family: "Inter", sans-serif;
  color: #8b8b8b;
  white-space: nowrap;
  align-self: start;
}

.shipment-value {
  grid-column: 2;
  min-width: 0;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 19px;
  font-weight: 400;
  font-size: 14px;
  color: #8b8b8b;
  align-self: start;
}

.shipment-row:nth-child(2) .shipment-value {
  grid-column: 1 / span 2;
  display: block;
  white-space: pre-line;
}

.shipment-copy {
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  align-self: center;
  margin-left: 4px;
  display: inline-flex;
  gap: 6px;
}

.shipment-copy .fa-copy {
  color: $pj-color-green;
}
</style>
